import React from 'react';
import styled from 'styled-components';

import {
  cleanupEmissionConsumptionMarkup,
  renderTextWithFootnotesReferencesV2,
} from '@oneaudi/feature-app-utils';
import { Text } from '@oneaudi/unified-web-components';
import type { CarlineConsumptionAndEmissionSummary } from '../../graphql/documents/carline-structure.generated';
import { useFeatureAppEnv } from '../context/FeatureAppEnvContext';

interface ConsumptionAndEmissionProps {
  consumptionData: CarlineConsumptionAndEmissionSummary;
  carlineName: string;
}

const ConsumptionAndEmission: React.FC<ConsumptionAndEmissionProps> = ({
  consumptionData,
  carlineName,
}) => {
  const logger = useFeatureAppEnv().featureServices['s2:logger'];

  const { consumption, emissionCO2, co2Class, dischargedCO2Class, dischargedConsumption } =
    consumptionData;

  const logMissingData = () => {
    if (!consumption) logger.warn(`Missing consumption data for ${carlineName}`);
    if (!emissionCO2) logger.warn(`Missing emission CO2 data for ${carlineName}`);
  };

  const fields = [
    { value: consumption, id: 'consumption' },
    { value: emissionCO2, id: 'emissionCO2' },
    { value: co2Class, id: 'co2Class' },
    { value: dischargedConsumption, id: 'dischargedConsumption' },
    { value: dischargedCO2Class, id: 'dischargedCO2Class' },
  ];

  const fontSize = process.env.IS_LEGACY ? '2' : '3';

  const validFields = fields.filter((field) => field.value !== null && field.value !== '');
  logMissingData();

  return (
    <StyledTextWithFootnotes
      variant={{
        order: fontSize,
        style: 'normal',
        type: 'copy',
      }}
      data-testid="cae"
    >
      {validFields.map((field, index, array) => (
        <React.Fragment key={field.id}>
          {renderTextWithFootnotesReferencesV2(cleanupEmissionConsumptionMarkup(field.value || ''))}
          {index < array.length - 1 && '; '}
        </React.Fragment>
      ))}
    </StyledTextWithFootnotes>
  );
};

export const StyledTextWithFootnotes = styled(Text)`
  > sup {
    position: relative;
    top: -1em;
    vertical-align: baseline;
  }
`;

export default ConsumptionAndEmission;
